<template>
    <TDataTable
        class="purchase-order-table"
        data-testid="purchase-order-list-table"
        :value="tableData"
        responsive-layout="scroll"
        scrollable
        :class="{isScrollingY}"
    >
        <TColumn v-for="column in columns" :key="column" :field="column" class="data-table-column" :class="[getColumnWidthClass(column)]">
            <template #header>
                <div :data-testid="`purchase-order-table-th-${column}`" class="data-table-column_header" :class="[`${column}-column`]">
                    <span class="data-table-column_header_label">
                        {{ t(`projectclient.purchaseorderlist.table.${column}.header`) }}
                    </span>
                </div>
            </template>
            <template #body="{data, index}">
                <PurchaseOrderTableCellWrapper :key="data.id" :column="column" :data="data" :index="index" />
            </template>
        </TColumn>

        <template #footer>
            <span>{{ t('projectclient.purchaseorderlist.table.footer.total', {count: totalElements}) }}</span>
        </template>
        <template #loading>
            <VJoySpinner />
        </template>
        <template #empty>
            <p>{{ t('projectclient.purchaseorderlist.table.empty') }}</p>
        </template>
    </TDataTable>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
    import {useUserStore} from '@common/stores';
    import {type PurchaseOrderDataTableColumn, usePurchaseOrderMonitoringStore} from '@features/purchase-order-monitoring';
    import {storeToRefs} from 'pinia';
    import {nextTick, useTranslation} from '#imports';
    import {createDataTableComponents} from '@malt/joy-entrypoint/src/vue/utils';
    import {useInfiniteScroll, useScrollContainer} from '@malt/utils';
    import {VJoySpinner} from '@maltjoy/core-vue';
    import type {PurchaseOrderMonitoringResource} from '@api';
    import type {PurchaseOrderDataTableColumnArray} from '~/features';
    import PurchaseOrderTableCellWrapper from './PurchaseOrderTableCellWrapper.vue';

    const {t} = useTranslation();
    const {TColumn, TDataTable} = createDataTableComponents<PurchaseOrderMonitoringResource>();

    defineProps<{
        tableData: PurchaseOrderMonitoringResource[];
        totalElements: number;
    }>();

    function getColumnWidthClass(column: string): string {
        return `${column}-column`;
    }

    const purchaseOrderListStore = usePurchaseOrderMonitoringStore();

    const datatableWrapperRef = ref<HTMLElement>();

    const {isScrollingY, startListening, stopListening} = useScrollContainer({
        target: datatableWrapperRef,
        immediate: false,
    });

    const {startObserving, stopObserving} = useInfiniteScroll(
        (_) => {
            // Load more projects
        },
        {immediate: false},
    );

    const columns = computed<PurchaseOrderDataTableColumnArray>(() => {
        const baseColumns: (PurchaseOrderDataTableColumn | undefined)[] = ['identifier', 'label', 'amount', 'status', 'creationDate'];

        return baseColumns.filter((column): column is PurchaseOrderDataTableColumn => !!column);
    });

    async function startObservingScrollAndInfinite() {
        await nextTick();
        datatableWrapperRef.value = document.querySelector('.p-datatable-table-container') as HTMLElement;
        startListening();
        startObserving();
    }

    onMounted(() => {
        startObservingScrollAndInfinite();
    });

    onBeforeUnmount(() => {
        stopListening();
        stopObserving();
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .purchase-order-table {
        width: 100%;
        height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        font-size: var(--joy-font-size-primary-300);
        overflow: auto;

        :deep(.p-datatable-table-container) {
            height: 100%;
        }

        :deep(tr:hover) {
            background-color: var(--joy-color-neutral-20);
        }

        :deep(th.data-table-column),
        :deep(td.data-table-column) {
            white-space: nowrap;
            text-overflow: ellipsis;
            min-width: 120px;
            padding: 0;
            height: 56px;

            &.identifier-column,
            &.label-column {
                width: 200px;
                max-width: 280px;
            }

            &.amount-column,
            &.creationDate-column {
                text-align: right;
            }
        }

        :deep(th.data-table-column) {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            border-top: 1px solid var(--joy-color-neutral-30);
            background-color: var(--joy-color-neutral-10);
            padding: 0 var(--joy-core-spacing-4);
        }

        :deep(.p-datatable-column-header-content) {
            width: 100%;
        }

        :deep(.data-table-column_header) {
            display: flex;
            min-width: 0;
            flex-flow: row nowrap;
            align-items: center;
            width: 100%;

            &.amount-column,
            &.creationDate-column {
                justify-content: flex-end;
            }

            &.label {
                @include text.getFontBySize(primary-350);

                color: var(--joy-color-text-default);
            }
        }

        :deep(.p-datatable-loading-overlay) {
            height: 100%;
            width: 100%;
            background-color: rgb(0 0 0 / 20%);
        }

        :deep(.p-datatable-emptymessage > td) {
            text-align: center;
            height: 56px;

            & > p {
                width: 100%;
            }
        }

        :deep(.p-datatable-footer) {
            background: var(--joy-color-neutral-10);
            border-top: 1px solid var(--joy-color-neutral-30) !important;
            color: var(--neutral-joy-color-neutral-50);
            font-weight: var(--joy-font-weight-normal);
        }

        &.isScrollingY {
            :deep(thead.p-datatable-thead) {
                box-shadow: -3px 0 12px 0 var(--joy-color-neutral-30);
            }

            :deep(tr:last-child td.data-table-column) {
                border-bottom: none;
            }
        }
    }
</style>
